import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import {
  extractTenantFromSubdomain,
  extractTenantFromQueryParam,
  validateTenantConsistency,
  validateTenantFormat,
} from '../../services/tenant.service';
import { debugWarn, debugError } from '../../utils/debugLog';

const Login = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Auto-redirect immediately to SSO login
  useEffect(() => {
    const hostname = window.location.hostname;
    const search = location.search;
    const returnPath = config.defaults.returnPath;

    // Extract tenant from subdomain (if tenant subdomain, not reserved)
    const tenantFromSubdomain = extractTenantFromSubdomain(hostname);

    // Extract tenant from query parameter
    const tenantFromQuery = extractTenantFromQueryParam(search);

    // Validate consistency - SECURITY: Subdomain is authoritative
    const { tenant, warning } = validateTenantConsistency(
      tenantFromSubdomain,
      tenantFromQuery
    );

    // Log security warning if mismatch detected
    if (warning) {
      debugWarn(warning);
    }

    // If no tenant from URL, try config default (localhost only)
    let finalTenant = tenant;
    if (
      !finalTenant &&
      (hostname === 'localhost' || hostname === '127.0.0.1')
    ) {
      finalTenant = config.defaults.tenantAlias;
    }

    // Validate tenant format
    if (finalTenant) {
      const formatValidation = validateTenantFormat(finalTenant);
      if (!formatValidation.valid) {
        debugError('Invalid tenant format:', formatValidation.error);
        navigate('/tenant-selection', { replace: true });
        return;
      }
      finalTenant = formatValidation.tenant;
    }

    if (!finalTenant) {
      debugError('Tenant alias not found in URL parameters or subdomain');
      // Redirect back to tenant selection if no tenant specified
      navigate('/tenant-selection', { replace: true });
      return;
    }

    // Redirect directly to backend SSO login with validated tenant
    const ssoUrl = `${config.urls.ssoLogin}?tenant=${finalTenant}&return_to=${encodeURIComponent(returnPath)}`;
    window.location.href = ssoUrl;
  }, [location.search, navigate]);

  // Redirect to prompt-dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/prompt-dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // tenantDisplayName is now a state variable loaded dynamically

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        // Soft background like other auth screens
        backgroundColor: theme.palette.background.default || '#f5f5f7',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          color: theme.palette.text.primary,
          maxWidth: 600,
          width: '90%',
          bgcolor: 'rgba(255,255,255,0.96)',
          borderRadius: 4,
          boxShadow: 6,
          px: { xs: 5, sm: 6 },
          py: { xs: 6, sm: 7 },
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CircularProgress
          size={80}
          sx={{
            color: theme.palette.primary.main,
            marginBottom: 4,
          }}
        />

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            marginBottom: 3,
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Redirecting to SSO Login...
        </Typography>

        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            lineHeight: 1.7,
            fontSize: { xs: '1rem', sm: '1.1rem' },
          }}
        >
          Please wait while we redirect you to the secure authentication page.
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
